import React from "react";

import { observer } from "mobx-react-lite";

import { useLang } from "@src/hooks/useLang";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Dropdown, { Option } from "@components/MapDropdown";
import type { Localizations } from "@src/localizations";
import { TOverallMapView, TFieldMapView } from "@screens/FieldsScreen/FieldsScreenVM";

/**
 * Sentinel value for the dropdown's "add" row. Deliberately not `custom:...`
 * so it can never be mistaken for a real layer id.
 */
export const ADD_CUSTOM_LAYER_VALUE = "__add_custom_layer__";

/**
 * The per-field layer menu, shared by this dropdown and the compare pane's
 * (CompareMapPanel), which carried a copy — "the menu must read the same on
 * both sides". Built-in layers, then the potato-camera layers when the account
 * has a camera AND the field has uploaded data, then the customer's own maps
 * (badged), and always last the "add custom layer" action.
 */
export function buildFieldViewOptions(
  lang: Localizations,
  opts: { cameraAvailable: boolean; customLayers: Array<{ id: string; name: string }> }
): Option[] {
  const options: Option[] = [
    { title: lang.fields.mapSelecterBoundaries, value: "border" },
    { title: lang.fields.mapSelecterYield, value: "yield" },
    { title: lang.fields.mapSelecterNdvi, value: "ndvi" },
    {
      title: lang.fields.mapSelecterBrightness,
      value: "brightness",
      info: lang.fields.mapSelecterBrightnessInfo,
    },
    {
      title: lang.fields.mapSelecterMoisture,
      value: "moisture",
      info: lang.fields.mapSelecterMoistureInfo,
    },
    { title: lang.fields.mapSelecterProductivity, value: "productivity" },
    {
      title: lang.fields.mapSelecterTracks,
      value: "tracks",
      info: lang.fields.mapSelecterTracksInfo,
    },
  ];

  // Potato-camera layers: only for users with the camera flag AND fields that
  // actually have uploaded camera data — everyone else sees the menu unchanged.
  if (opts.cameraAvailable) {
    options.push(
      { title: lang.fields.mapSelecterPotatoCount, value: "potato_count" },
      { title: lang.fields.mapSelecterStonePercent, value: "stone_percent" },
      { title: lang.fields.mapSelecterPotatoSize, value: "potato_size" }
    );
  }

  // Customer-supplied maps go after every built-in layer, each badged so it is
  // obvious which rows are imported rather than computed by us.
  opts.customLayers.forEach((layer) => {
    options.push({
      title: layer.name,
      value: `custom:${layer.id}`,
      badge: lang.fields.customLayerImportedBadge,
    });
  });

  // Always the very last row: the way in. Without it a field with no custom
  // layers would have no entry point at all. An action, not a view.
  options.push({
    title: lang.fields.customLayerAdd,
    value: ADD_CUSTOM_LAYER_VALUE,
    action: true,
  });
  return options;
}

interface IProps { }

const FieldViewSelect: React.FC<IProps> = () => {
  const lang = useLang();
  const vm = useFieldsScreenVM();

  // Overall map view (no field selected): Crop type / Average yield
  if (vm.selectedFieldId == null) {
    return (
      <Dropdown
        title={lang.fields.fieldViewTitle}
        options={[
          {
            title: lang.fields.mapSelecterCropType ?? "Crop type",
            value: "crop",
          },
          {
            title: lang.fields.mapSelecterAvgYield ?? "Average yield",
            value: "avgYield",
          },
        ]}
        selected={vm.overallMapView}
        onSelect={(value: string) => {
          // Tracking lives in the VM so programmatic and sticky-layer
          // renders are counted too, not just dropdown choices.
          vm.setOverallMapView(value as TOverallMapView);
        }}
      />
    );
  }

  // Per-field view: Boundaries / Yield / NDVI (+ camera layers when available)
  const options = buildFieldViewOptions(lang, {
    cameraAvailable: vm.cameraAvailable,
    customLayers: vm.customLayers,
  });

  return (
    <Dropdown
      title={lang.fields.fieldViewTitle}
      options={options}
      selected={vm.selectedFieldView}
      onSelect={(value: string) => {
        // "Add custom layer" is an action, not a layer — open the wizard and
        // leave whatever is on screen exactly where it is.
        if (value === ADD_CUSTOM_LAYER_VALUE) {
          vm.openCustomLayerDialog();
          return;
        }
        vm.setSelectedFieldView(value as TFieldMapView);
      }}
    />
  );
};

export default observer(FieldViewSelect);
